import React, {
  memo, useEffect, useMemo, useRef, useState,
} from 'react';
import * as Recharts from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFaceSmile, faUsers } from '@fortawesome/pro-regular-svg-icons';

import Info from '../../../components/Info';
import {
  GENDER_FEMALE_COLOR,
  GENDER_MALE_COLOR,
  getAgeBucketColor,
} from '../../Analytics/CrowdCountSection/constants';
import { FACE, PERSON, fmtTileRC } from '../helpers';
import style from './style.module.scss';
import {
  ATTR_COLOR,
  LABEL_BOX_COLOR,
  binsToSeries,
  emotionColor,
  formatPct,
  openLabelDetail,
  rankAgeRanges,
  sectionAgeDetail,
  titleCase,
} from '../compute';

const AXIS_TICK = { fontSize: 11, fill: '#4E5155' };
const RECHART_TOOLTIP_STYLE = {
  borderRadius: 8,
  border: '1px solid #E8E8E8',
  boxShadow: '0 6px 20px rgba(135, 135, 136, 0.14)',
  fontSize: 12,
};
const CONF_COLORS = {
  face: '#4E9BF6',
  person: '#FFAE20',
  gender: '#7E8ED9',
};

const RADIAN = Math.PI / 180;

const renderPieLabel = ({
  cx, cy, midAngle, outerRadius, percent, name, value,
}) => {
  if (!percent || percent < 0.03) return null;
  const radius = outerRadius + 22;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text
      x={x}
      y={y}
      fill="#4E5155"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      fontSize={12}
      fontWeight={500}
    >
      {`${name} ${value} (${Math.round(percent * 100)}%)`}
    </text>
  );
};

const Section = ({ title, children, className }) => (
  <section className={[style.section, className].filter(Boolean).join(' ')}>
    {title ? <h5 className={style.sectionTitle}>{title}</h5> : null}
    {children}
  </section>
);

const StatStrip = ({ stats }) => {
  const { persons, faces } = stats.counts || {};
  const items = [
    {
      label: 'Persons',
      value: persons || 0,
      icon: faUsers,
      tone: 'persons',
      info: 'Person label instances (DetectLabels). Not deduplicated across tiles.',
    },
    {
      label: 'Faces',
      value: faces || 0,
      icon: faFaceSmile,
      tone: 'faces',
      info: 'Face detections (DetectFaces). Independent of person count.',
    },
  ];
  return (
    <div className={style.statStrip}>
      {items.map(({
        label, value, icon, tone, info,
      }) => (
        <div
          key={label}
          className={[style.statCard, tone && style[`statCard_${tone}`]].filter(Boolean).join(' ')}
        >
          {icon ? (
            <div className={style.statIcon} aria-hidden>
              <FontAwesomeIcon icon={icon} />
            </div>
          ) : null}
          <div className={style.statBody}>
            <div className={style.statValue}>{value}</div>
            <div className={style.statLabel}>{label}</div>
          </div>
          {info ? <Info content={info} className={style.statInfo} /> : null}
        </div>
      ))}
    </div>
  );
};

const ChartCard = ({
  title, info, meta, actions, children, className, printBreak,
}) => (
  <div
    className={[
      style.chartCard,
      printBreak ? 'print-break-page' : null,
      className,
    ].filter(Boolean).join(' ')}
  >
    {(title || meta || actions || info) ? (
      <div className={style.chartHeader}>
        {title ? <span className={style.chartTitle}>{title}</span> : <span />}
        <div className={style.chartHeaderRight}>
          {meta ? <span className={style.chartMeta}>{meta}</span> : null}
          {actions}
          {info ? <Info content={info} /> : null}
        </div>
      </div>
    ) : null}
    {children}
  </div>
);

const formatCountTick = (v) => {
  if (v >= 10000) return `${Math.round(v / 1000)}k`;
  if (v >= 1000) return `${(v / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  return String(v);
};

const YScaleToggle = ({ value, onChange }) => (
  <fieldset className={style.scaleToggle} aria-label="Y axis scale">
    <button
      type="button"
      className={value === 'log' ? style.scaleToggleActive : undefined}
      onClick={() => onChange('log')}
    >
      Log
    </button>
    <button
      type="button"
      className={value === 'linear' ? style.scaleToggleActive : undefined}
      onClick={() => onChange('linear')}
    >
      Linear
    </button>
  </fieldset>
);

const logYAxisProps = {
  scale: 'log',
  domain: [1, (dataMax) => Math.max(Number(dataMax) || 1, 1)],
  allowDataOverflow: true,
  tickFormatter: formatCountTick,
};

const linearYAxisProps = {
  allowDecimals: false,
  tickFormatter: formatCountTick,
};

let confidenceGradientSeq = 0;

const CONF_KIND = {
  face: {
    title: 'Face',
    color: CONF_COLORS.face,
    info: 'Confidence of detected faces.',
  },
  person: {
    title: 'Person',
    color: CONF_COLORS.person,
    info: 'Confidence of detected people.',
  },
  gender: {
    title: 'Gender',
    color: CONF_COLORS.gender,
    info: 'Confidence of detected gender.',
  },
};

const ConfidenceChart = ({ stats, kind }) => {
  const { title, color, info } = CONF_KIND[kind];
  const bins = stats.confidence?.[kind] || [];
  const data = useMemo(() => binsToSeries(bins), [bins]);
  confidenceGradientSeq += 1;
  const gid = useRef(`g-${confidenceGradientSeq}`).current;
  const [yScale, setYScale] = useState('log');
  const total = bins.reduce((sum, n) => sum + n, 0);
  const hasData = total > 0;
  const isLog = yScale === 'log';
  const yAxisProps = isLog ? logYAxisProps : linearYAxisProps;

  return (
    <ChartCard
      title={title}
      info={info}
      actions={hasData ? <YScaleToggle value={yScale} onChange={setYScale} /> : null}
    >
      {!hasData ? (
        <div className={style.emptyChart}>No data</div>
      ) : (
        <div className={style.rechartWrap}>
          <Recharts.ResponsiveContainer width="100%" height={200}>
            <Recharts.AreaChart
              data={data}
              margin={{
                top: 8, right: 8, left: 0, bottom: 0,
              }}
            >
              <defs>
                <linearGradient id={`conf-${gid}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.45} />
                  <stop offset="100%" stopColor={color} stopOpacity={0.04} />
                </linearGradient>
              </defs>
              <Recharts.CartesianGrid strokeDasharray="3 3" stroke="#E8E8E8" vertical={false} />
              <Recharts.XAxis
                dataKey="pct"
                type="number"
                domain={[0, 99]}
                ticks={[0, 25, 50, 75, 99]}
                tickFormatter={(v) => (v === 99 ? '100' : String(v))}
                tick={AXIS_TICK}
                axisLine={{ stroke: '#B7B8BA' }}
                tickLine
              />
              <Recharts.YAxis
                width={42}
                tick={AXIS_TICK}
                axisLine={{ stroke: '#B7B8BA' }}
                tickLine
                {...yAxisProps}
              />
              <Recharts.Tooltip
                cursor={{ stroke: 'rgba(78, 81, 85, 0.25)', strokeWidth: 1 }}
                contentStyle={RECHART_TOOLTIP_STYLE}
                labelFormatter={(pct) => `Confidence ${pct}%`}
                formatter={(_value, _name, item) => [item?.payload?.count ?? 0, 'Count']}
              />
              <Recharts.Area
                type="monotone"
                dataKey={isLog ? 'y' : 'count'}
                stroke={color}
                strokeWidth={2}
                fill={`url(#conf-${gid})`}
                baseValue={isLog ? 1 : 0}
                connectNulls
                isAnimationActive={false}
              />
            </Recharts.AreaChart>
          </Recharts.ResponsiveContainer>
        </div>
      )}
    </ChartCard>
  );
};

const DistributionPie = ({ stats }) => {
  const slices = useMemo(() => ([
    { name: 'Male', value: stats.gender?.Male || 0, fill: GENDER_MALE_COLOR },
    { name: 'Female', value: stats.gender?.Female || 0, fill: GENDER_FEMALE_COLOR },
  ].filter(({ value }) => value > 0)), [stats.gender]);
  const total = slices.reduce((sum, s) => sum + s.value, 0);
  const hasData = total > 0;

  return (
    <ChartCard
      title="Gender Demographics"
      info="Male/female breakdown from detected faces. Faces without gender are excluded."
    >
      {!hasData ? (
        <div className={style.emptyChart}>No gender data</div>
      ) : (
        <div className={style.rechartWrap}>
          <Recharts.ResponsiveContainer width="100%" height={320}>
            <Recharts.PieChart margin={{
              top: 12, right: 16, bottom: 8, left: 16,
            }}
            >
              <Recharts.Pie
                data={slices}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={48}
                outerRadius={82}
                paddingAngle={2}
                isAnimationActive={false}
                label={renderPieLabel}
                labelLine={{ stroke: '#B7B8BA', strokeWidth: 1 }}
              >
                {slices.map((slice) => (
                  <Recharts.Cell key={slice.name} fill={slice.fill} />
                ))}
              </Recharts.Pie>
              <Recharts.Tooltip
                contentStyle={RECHART_TOOLTIP_STYLE}
                formatter={(value, name) => [value, name]}
              />
              <Recharts.Legend
                iconType="circle"
                iconSize={10}
                wrapperStyle={{ fontSize: '13px', color: '#4E5155' }}
              />
            </Recharts.PieChart>
          </Recharts.ResponsiveContainer>
        </div>
      )}
    </ChartCard>
  );
};


const AgeHistogram = ({ stats, mode = 'buckets', printBreak }) => {
  const { age } = stats;
  const rows = useMemo(() => {
    const raw = mode === 'ranges' ? (age?.ranges || []) : (age?.buckets || []);
    return raw.map((row, index) => ({
      ...row,
      fill: mode === 'ranges' ? '#4E9BF6' : getAgeBucketColor(row.name, index),
    }));
  }, [age, mode]);
  const title = mode === 'ranges'
    ? 'Age Distribution (Ranges)'
    : 'Age Distribution (Midpoints)';
  const info = mode === 'ranges'
    ? 'Estimated age range for each detected face.'
    : 'Estimated ages from detected faces, grouped into age brackets.';
  const total = rows.reduce((sum, r) => sum + r.value, 0);
  const hasData = total > 0;
  const many = rows.length > 10;
  const height = many ? Math.min(360, 220 + Math.min(rows.length, 20) * 4) : 260;

  return (
    <ChartCard title={title} info={info} printBreak={printBreak}>
      {!hasData ? (
        <div className={style.emptyChart}>No age data</div>
      ) : (
        <div className={style.rechartWrap}>
          <Recharts.ResponsiveContainer width="100%" height={height}>
            <Recharts.BarChart
              data={rows}
              margin={{
                top: 8, right: 16, bottom: many ? 48 : 8, left: 8,
              }}
            >
              <Recharts.CartesianGrid strokeDasharray="3 3" stroke="#E8E8E8" vertical={false} />
              <Recharts.XAxis
                dataKey="name"
                tick={AXIS_TICK}
                axisLine={{ stroke: '#B7B8BA' }}
                tickLine
                interval={many ? 'preserveStartEnd' : 0}
                angle={many ? -35 : 0}
                textAnchor={many ? 'end' : 'middle'}
                height={many ? 60 : 30}
              />
              <Recharts.YAxis
                width={44}
                tick={AXIS_TICK}
                axisLine={{ stroke: '#B7B8BA' }}
                tickLine
                label={{
                  value: 'Count',
                  angle: -90,
                  position: 'insideLeft',
                  style: { fill: '#4E5155', fontSize: 12, textAnchor: 'middle' },
                }}
                {...linearYAxisProps}
              />
              <Recharts.Tooltip
                cursor={{ fill: 'rgba(78, 81, 85, 0.06)' }}
                contentStyle={RECHART_TOOLTIP_STYLE}
                formatter={(value) => [value, 'Faces']}
              />
              <Recharts.Bar dataKey="value" name="Faces" radius={[10, 10, 0, 0]} isAnimationActive={false}>
                {rows.map((row) => (
                  <Recharts.Cell key={row.name} fill={row.fill} />
                ))}
              </Recharts.Bar>
            </Recharts.BarChart>
          </Recharts.ResponsiveContainer>
        </div>
      )}
    </ChartCard>
  );
};

const RANK_Y_WIDTH = 120;
const RANK_ROW_PX = 36;
const RANK_LABEL_RIGHT = 88;

const RankEndLabel = ({
  x, y, width, height, viewBox, endPct, endCount, value,
}) => {
  const pct = endPct ?? (typeof value === 'string' ? value : value?.pct);
  const count = endCount ?? (typeof value === 'object' && value != null ? value.count : null);
  if (pct == null && count == null) return null;
  const bx = Number(x ?? viewBox?.x);
  const by = Number(y ?? viewBox?.y);
  const bw = Number(width ?? viewBox?.width ?? 0);
  const bh = Number(height ?? viewBox?.height ?? 0);
  const px = bx + bw + 8;
  const py = by + bh / 2;
  if (!Number.isFinite(px) || !Number.isFinite(py)) return null;
  const label = [pct, count].filter((v) => v != null).join(' ');
  return (
    <text
      x={px}
      y={py}
      textAnchor="start"
      dominantBaseline="central"
      fill="#4E5155"
      fontSize={11}
      fontWeight={600}
      style={{ pointerEvents: 'none' }}
    >
      {label}
    </text>
  );
};

const RankBarChart = ({
  rows,
  dataKey = 'value',
  seriesName = 'Count',
  formatter,
  onBarClick,
  emptyText = 'No data',
  xDomain,
  xTickFormatter,
  stickyAxis = false,
}) => {
  const height = Math.max(200, 24 + rows.length * RANK_ROW_PX);
  const hasEndLabels = rows.some((r) => r.endPct != null || r.endCount != null);
  const rightPad = hasEndLabels ? RANK_LABEL_RIGHT : 24;
  const dataMax = Math.max(0, ...rows.map((r) => Number(r[dataKey]) || 0));
  const axisDomain = xDomain || [0, Math.max(dataMax, 1)];

  if (!rows.length) {
    return <div className={style.emptyChart}>{emptyText}</div>;
  }

  const sharedMargin = {
    top: 8,
    right: rightPad,
    bottom: stickyAxis ? 4 : 8,
    left: 8,
  };

  const renderEndLabel = (props) => {
    const row = rows[props.index];
    return (
      <RankEndLabel
        {...props}
        endPct={row?.endPct}
        endCount={row?.endCount}
      />
    );
  };

  const bars = (
    <Recharts.Bar
      dataKey={dataKey}
      name={seriesName}
      radius={[0, 10, 10, 0]}
      minPointSize={6}
      isAnimationActive={false}
      cursor={onBarClick ? 'pointer' : 'default'}
      onClick={onBarClick ? (item) => onBarClick(item?.payload || item) : undefined}
    >
      {rows.map((row) => (
        <Recharts.Cell key={row.name} fill={row.fill} />
      ))}
      {hasEndLabels ? (
        <Recharts.LabelList dataKey="endPct" content={renderEndLabel} />
      ) : null}
    </Recharts.Bar>
  );

  if (!stickyAxis) {
    return (
      <div className={style.rechartWrap} style={{ height }}>
        <Recharts.ResponsiveContainer width="100%" height={height}>
          <Recharts.BarChart layout="vertical" data={rows} margin={sharedMargin}>
            <Recharts.CartesianGrid strokeDasharray="3 3" stroke="#E8E8E8" horizontal={false} />
            <Recharts.XAxis
              type="number"
              tick={AXIS_TICK}
              axisLine={{ stroke: '#B7B8BA' }}
              tickLine
              domain={axisDomain}
              tickFormatter={xTickFormatter}
              allowDecimals={false}
            />
            <Recharts.YAxis
              type="category"
              dataKey="name"
              width={RANK_Y_WIDTH}
              tick={AXIS_TICK}
              axisLine={{ stroke: '#B7B8BA' }}
              tickLine
              interval={0}
            />
            <Recharts.Tooltip
              cursor={{ fill: 'rgba(78, 81, 85, 0.06)' }}
              contentStyle={RECHART_TOOLTIP_STYLE}
              formatter={formatter || ((value) => [value, seriesName])}
            />
            {bars}
          </Recharts.BarChart>
        </Recharts.ResponsiveContainer>
      </div>
    );
  }

  return (
    <div className={style.rankBarShell}>
      <div className={style.rankBarScroll}>
        <div className={style.rechartWrap} style={{ height }}>
          <Recharts.ResponsiveContainer width="100%" height={height}>
            <Recharts.BarChart layout="vertical" data={rows} margin={sharedMargin}>
              <Recharts.CartesianGrid strokeDasharray="3 3" stroke="#E8E8E8" horizontal={false} />
              <Recharts.XAxis type="number" hide domain={axisDomain} />
              <Recharts.YAxis
                type="category"
                dataKey="name"
                width={RANK_Y_WIDTH}
                tick={AXIS_TICK}
                axisLine={{ stroke: '#B7B8BA' }}
                tickLine
                interval={0}
              />
              <Recharts.Tooltip
                cursor={{ fill: 'rgba(78, 81, 85, 0.06)' }}
                contentStyle={RECHART_TOOLTIP_STYLE}
                formatter={formatter || ((value) => [value, seriesName])}
              />
              {bars}
            </Recharts.BarChart>
          </Recharts.ResponsiveContainer>
        </div>
      </div>
      <div className={style.rankBarAxisFooter}>
        <Recharts.ResponsiveContainer width="100%" height={40}>
          <Recharts.BarChart
            data={[{ [dataKey]: dataMax }]}
            margin={{
              top: 8, right: rightPad, bottom: 0, left: 8 + RANK_Y_WIDTH,
            }}
          >
            <Recharts.XAxis
              type="number"
              dataKey={dataKey}
              domain={axisDomain}
              tick={AXIS_TICK}
              axisLine={{ stroke: '#B7B8BA' }}
              tickLine
              tickFormatter={xTickFormatter}
              allowDecimals={false}
            />
          </Recharts.BarChart>
        </Recharts.ResponsiveContainer>
      </div>
    </div>
  );
};

const EmotionsChart = ({ stats }) => {
  const entries = Object.entries(stats.emotions || {})
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  const total = entries.reduce((sum, [, n]) => sum + n, 0);
  const rows = entries.map(([name, count]) => ({
    name: titleCase(name),
    value: count,
    fill: emotionColor(name),
    endPct: formatPct(count, total),
    endCount: count,
  }));

  return (
    <ChartCard
      title="Emotion Distribution"
      info="The highest registered emotion for each detected face."
      meta={total ? `${total} faces` : null}
    >
      <RankBarChart
        rows={rows}
        seriesName="Faces"
        emptyText="No emotions"
        formatter={(value) => [`${value} (${formatPct(value, total)})`, 'Faces']}
      />
    </ChartCard>
  );
};

const FaceAttributesChart = ({ stats }) => {
  const faces = stats.counts?.faces || 0;
  const rows = useMemo(() => (stats.faceAttrs || []).map((r) => ({
    name: r.name,
    value: Math.round((r.rate || 0) * 1000) / 10,
    present: r.present,
    known: r.known,
    fill: ATTR_COLOR,
    endPct: formatPct(r.present, r.known),
    endCount: r.present,
  })), [stats.faceAttrs]);
  return (
    <ChartCard
      title="Face Attributes"
      info="Percent of detected faces with each trait, such as smile, glasses, or beard."
      meta={faces ? `${faces} faces` : null}
    >
      <RankBarChart
        rows={rows}
        seriesName="Rate"
        emptyText="No attributes"
        xDomain={[0, 100]}
        xTickFormatter={(v) => `${v}%`}
        formatter={(_value, _name, item) => {
          const row = item?.payload;
          if (!row) return [_value, 'Rate'];
          return [
            `${row.present} of ${row.known} (${formatPct(row.present, row.known)})`,
            'Present',
          ];
        }}
      />
    </ChartCard>
  );
};

const COVERAGE_STOPS = ['#8EBFEC', '#6AABE4', '#5A9FE3', '#3F88CC', '#2F6FB5', '#1A406E'];

const coverageFill = (count, max, stops) => {
  if (!count || !max) return stops[0];
  const t = Math.min(1, count / max);
  const idx = Math.min(stops.length - 1, Math.round(t * (stops.length - 1)));
  return stops[idx];
};

const TileHeatmap = ({
  stats, kind = 'faces', onCellClick, printBreak,
}) => {
  const { coverage } = stats;
  const rows = coverage?.rows || 0;
  const cols = coverage?.cols || 0;
  const isPersons = kind === PERSON || kind === 'persons';
  const grid = isPersons ? (coverage?.persons || []) : (coverage?.faces || []);
  const title = isPersons ? 'Persons' : 'Faces';
  const info = isPersons
    ? 'Person label instances per tile in the scan grid.'
    : 'Face detections per tile in the scan grid.';
  const flat = (grid || []).flat();
  const max = Math.max(1, ...flat);
  const total = flat.reduce((s, n) => s + n, 0);
  const cells = [];

  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < cols; c += 1) {
      const count = grid[r]?.[c] || 0;
      const bg = coverageFill(count, max, COVERAGE_STOPS);
      cells.push(
        <button
          type="button"
          key={`r${r}-c${c}`}
          className={style.heatmapCell}
          style={{ background: bg }}
          title={fmtTileRC(r, c)}
          aria-label={fmtTileRC(r, c)}
          onClick={() => onCellClick?.(r, c)}
        >
          <span className={style.heatmapCount}>{count}</span>
        </button>,
      );
    }
  }

  const meta = total ? `${rows}×${cols} · ${total} total` : `${rows}×${cols}`;

  return (
    <ChartCard title={title} info={info} meta={meta} printBreak={printBreak}>
      {!rows || !cols ? (
        <div className={style.emptyChart}>No tile grid</div>
      ) : (
        <div
          className={style.heatmap}
          style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
        >
          {cells}
        </div>
      )}
    </ChartCard>
  );
};

const LabelsExplorer = ({ stats, onOpenDetail }) => {
  const { labels: catalog = [] } = stats;
  const boxedTotal = catalog.reduce((s, e) => s + e.count, 0);
  const rows = catalog.filter((e) => e.count > 0).map((entry) => ({
    name: entry.name,
    value: entry.count,
    fill: LABEL_BOX_COLOR,
    entry,
    endPct: formatPct(entry.count, boxedTotal),
    endCount: entry.count,
  }));

  if (!rows.length) {
    return (
      <ChartCard
        title="Label Distribution"
        info="How often each label appears in the scan, from most common to least."
      >
        <div className={style.emptyChart}>No labels</div>
      </ChartCard>
    );
  }

  return (
    <ChartCard
      title="Label Distribution"
      info="How often each label appears in the scan, from most common to least."
      meta={`${rows.length} labels · ${boxedTotal} boxes`}
    >
      <RankBarChart
        rows={rows}
        seriesName="Boxes"
        stickyAxis
        formatter={(value) => [`${value} (${formatPct(value, boxedTotal)})`, 'Boxes']}
        onBarClick={(payload) => openLabelDetail(payload?.entry, onOpenDetail)}
      />
    </ChartCard>
  );
};


/** YlGnBu — t in 0..1 */
const ageHeat = (t) => {
  const stops = [[255, 255, 217], [199, 233, 180], [65, 182, 196], [34, 94, 168], [8, 29, 88]];
  const x = Math.max(0, Math.min(1, t)) * (stops.length - 1);
  const i = Math.min(stops.length - 2, Math.floor(x));
  const f = x - i;
  const a = stops[i];
  const b = stops[i + 1];
  return `rgb(${a.map((v, j) => Math.round(v + (b[j] - v) * f)).join(',')})`;
};

const TIP_HIDE_MS = 280;
const AGE_TIP_WIDTH = 216;

const tipAt = (e, data) => ({ ...data, x: e.clientX, y: e.clientY });

const anchorRowTip = (el) => {
  if (!el || typeof el.getBoundingClientRect !== 'function') {
    return { x: 12, y: 12 };
  }
  const r = el.getBoundingClientRect();
  const pad = 8;
  let x = r.right - 2;
  if (x + AGE_TIP_WIDTH > window.innerWidth - pad) {
    x = Math.max(pad, r.left - AGE_TIP_WIDTH + 2);
  }
  const y = Math.max(pad, Math.min(r.top, window.innerHeight - 280));
  return { x, y };
};

const Tip = ({
  x, y, wide, interactive, onMouseEnter, onMouseLeave, children,
}) => (
  <div
    className={[
      style.pointerTip,
      wide && style.pointerTipWide,
      interactive && style.pointerTipInteractive,
    ].filter(Boolean).join(' ')}
    style={{ left: x, top: y }}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
  >
    {children}
  </div>
);

const AGE_TIP_TOP = 5;

const AgeTip = ({
  name, age, overlap, ageRanges, x, y, onMouseEnter, onMouseLeave, onOpenFull,
}) => {
  const ranked = rankAgeRanges(ageRanges);
  const max = Math.max(1, ...ranked.map((r) => r.n));
  const people = ranked.reduce((s, r) => s + r.n, 0);
  const shown = ranked.slice(0, AGE_TIP_TOP);
  const more = ranked.length - shown.length;

  return (
    <Tip
      x={x}
      y={y}
      wide
      interactive
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {age != null && (
        <div className={style.ageTipHead}>
          {`Age ${age} · ${overlap} age ${overlap === 1 ? 'estimate' : 'estimates'} at this year`}
        </div>
      )}
      <div className={style.ageTipHead}>{`Section ${name} · ${people} people`}</div>
      {shown.map((r) => (
        <div key={r.range} className={style.ageTipRow}>
          <span>{r.range}</span>
          <span className={style.ageTipTrack}>
            <span style={{ width: `${(r.n / max) * 100}%` }} />
          </span>
          <span>{r.n}</span>
        </div>
      ))}
      <button
        type="button"
        className={style.ageTipMore}
        onClick={(e) => {
          e.stopPropagation();
          onOpenFull?.();
        }}
      >
        {more > 0 ? `View full list (${more} more)` : 'View details'}
      </button>
    </Tip>
  );
};

const SplitChart = ({
  title, info, rows, aColor, bColor, aLabel, bLabel, segPct,
}) => {
  const [tip, setTip] = useState(null);

  return (
    <ChartCard title={title} info={info}>
      <div className={style.sectionRowsScroll}>
        <div className={style.ageCover}>
          {rows.map((r) => (
            <div
              key={r.name}
              className={style.ageCoverRow}
              onMouseMove={(e) => setTip(tipAt(e, { lines: r.tip }))}
              onMouseLeave={() => setTip(null)}
            >
              <span className={style.ageCoverLabel}>{r.name}</span>
              <div className={style.ageCoverMain}>
                {r.end ? (
                  <div className={style.splitLine}>
                    <div className={style.splitBar} style={{ width: `${r.width}%` }}>
                      <span style={{ flex: r.a || 0, background: aColor }} />
                      <span style={{ flex: r.b || 0, background: bColor }} />
                      {r.pct ? <span className={style.splitPct}>{r.pct}</span> : null}
                    </div>
                    <span className={style.splitEnd}>{r.end}</span>
                  </div>
                ) : (
                  <div className={style.splitBar} style={{ width: `${r.width}%` }}>
                    <span className={style.splitSeg} style={{ flex: r.a || 0, background: aColor }}>
                      {segPct && r.a ? `${r.a}%` : null}
                    </span>
                    <span className={style.splitSeg} style={{ flex: r.b || 0, background: bColor }}>
                      {segPct && r.b ? `${r.b}%` : null}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      {tip?.lines?.length ? (
        <Tip x={tip.x} y={tip.y}>
          {tip.lines.map((line) => (
            <div key={line} className={style.sectionTipLine}>{line}</div>
          ))}
        </Tip>
      ) : null}
      <div className={style.sectionRowsFooter}>
        <div className={style.splitLegend}>
          <span>
            <span className={style.splitSwatch} style={{ background: aColor }} />
            {aLabel}
          </span>
          <span>
            <span className={style.splitSwatch} style={{ background: bColor }} />
            {bLabel}
          </span>
        </div>
      </div>
    </ChartCard>
  );
};

const SectionDemographics = ({ stats, onOpenDetail }) => {
  const demo = stats?.sections || {};
  if (!(demo.occupancy?.length)) return null;
  const age = demo.age || {};
  const [ageTip, setAgeTip] = useState(null);
  const hideTimer = useRef(0);
  const {
    minAge = 0, span = 1, ticks = [], maxCover = 1, hasAges = false, rows: ageRows = [],
  } = age;

  const clearHide = () => {
    if (hideTimer.current) {
      window.clearTimeout(hideTimer.current);
      hideTimer.current = 0;
    }
  };

  const scheduleHide = () => {
    clearHide();
    hideTimer.current = window.setTimeout(() => setAgeTip(null), TIP_HIDE_MS);
  };

  useEffect(() => () => {
    if (hideTimer.current) window.clearTimeout(hideTimer.current);
  }, []);

  const openAgeDetail = (row, ageYear, overlap) => {
    clearHide();
    setAgeTip(null);
    const detail = sectionAgeDetail(row, {
      age: Number.isFinite(ageYear) ? ageYear : undefined,
      overlap: Number.isFinite(ageYear) ? overlap : undefined,
    });
    if (detail) onOpenDetail?.(detail);
  };

  return (
    <section className={style.section}>
      <h5 className={style.sectionTitle}>Section Demographics</h5>
      <div className={style.stack}>
        <ChartCard
          title="Age by Section"
          info="Estimated ages for faces centered on a seat square in each section. Heatmap spreads each age range across its years."
          className={style.ageChartCard}
        >
          {!hasAges ? (
            <div className={style.emptyChart}>No age ranges on this scan.</div>
          ) : (
            <>
              <div
                className={style.sectionRowsScroll}
                onScroll={() => {
                  clearHide();
                  setAgeTip(null);
                }}
              >
                <div className={style.ageCover}>
                  {ageRows.map((row) => (
                    <div
                      key={row.key}
                      className={style.ageCoverRow}
                      role="button"
                      tabIndex={0}
                      onMouseMove={(e) => {
                        clearHide();
                        const raw = e.target.dataset.age;
                        const tipAge = raw != null ? Number(raw) : undefined;
                        const pos = anchorRowTip(e.currentTarget);
                        setAgeTip({
                          row,
                          name: row.name,
                          ageRanges: row.ageRanges,
                          age: tipAge,
                          overlap: tipAge != null ? row.cover[tipAge] || 0 : undefined,
                          x: pos.x,
                          y: pos.y,
                        });
                      }}
                      onMouseLeave={scheduleHide}
                      onClick={(e) => {
                        const raw = e.target.dataset.age;
                        const tipAge = raw != null ? Number(raw) : undefined;
                        openAgeDetail(
                          row,
                          Number.isFinite(tipAge) ? tipAge : undefined,
                          Number.isFinite(tipAge) ? row.cover[tipAge] || 0 : undefined,
                        );
                      }}
                      onKeyDown={(e) => {
                        if (e.key !== 'Enter' && e.key !== ' ') return;
                        e.preventDefault();
                        openAgeDetail(row);
                      }}
                    >
                      <span className={style.ageCoverLabel}>{row.name}</span>
                      <div className={style.ageCoverMain}>
                        <div className={style.ageCoverBar}>
                          {Array.from(
                            { length: span + 1 },
                            (_, a) => minAge + a,
                          ).map((tipAge) => {
                            const n = row.cover[tipAge] || 0;
                            return (
                              <span
                                key={tipAge}
                                data-age={tipAge}
                                className={style.ageCoverCell}
                                style={n ? { background: ageHeat(n / maxCover) } : undefined}
                              />
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {ageTip ? (
                <AgeTip
                  {...ageTip}
                  onMouseEnter={clearHide}
                  onMouseLeave={scheduleHide}
                  onOpenFull={() => openAgeDetail(ageTip.row, ageTip.age, ageTip.overlap)}
                />
              ) : null}
              <div className={style.sectionRowsFooter}>
                <div className={style.ageCoverAxis}>
                  <span />
                  <div className={style.ageCoverTicks}>
                    {ticks.map((tipAge) => (
                      <span
                        key={tipAge}
                        className={style.ageCoverTick}
                        style={{ left: `${((tipAge - minAge) / span) * 100}%` }}
                      >
                        {tipAge}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </ChartCard>

        <SplitChart
          title="Occupancy by Section"
          info="Seats with a face or person detection centered on the seat square."
          rows={demo.occupancy || []}
          aColor="#4E9BF6"
          bColor="#D8DBE0"
          aLabel="Filled"
          bLabel="Empty"
        />

        <SplitChart
          title="Gender by Section"
          info="Male/female mix among faces centered on occupied seat squares."
          rows={demo.gender || []}
          aColor="#4E9BF6"
          bColor="#F9557D"
          aLabel="Male"
          bLabel="Female"
          segPct
        />
      </div>
    </section>
  );
};

const DataExplore = ({ data, onOpenDetail, onOpenTileCoverage }) => {
  const { stats, preview } = data;
  const { isGroup } = preview;

  return (
    <div className={style.explore}>
      <StatStrip stats={stats} />

      <Section title="Gender">
        <DistributionPie stats={stats} />
      </Section>

      <Section title="Age">
        <div className={style.stack}>
          <AgeHistogram stats={stats} />
          <AgeHistogram stats={stats} mode="ranges" printBreak />
        </div>
      </Section>

      <SectionDemographics stats={stats} onOpenDetail={onOpenDetail} />

      <Section title="Confidence" className={style.sectionSpaced}>
        <div className={style.grid3}>
          <ConfidenceChart stats={stats} kind="face" />
          <ConfidenceChart stats={stats} kind="person" />
          <ConfidenceChart stats={stats} kind="gender" />
        </div>
      </Section>

      <Section title="Emotions" className="print-break-page">
        <EmotionsChart stats={stats} />
      </Section>

      <Section title="Face attributes">
        <FaceAttributesChart stats={stats} />
      </Section>

      {!isGroup ? (
        <Section title="Per-tile coverage">
          <div className={style.grid2}>
            <TileHeatmap
              stats={stats}
              kind="faces"
              onCellClick={(r, c) => onOpenTileCoverage?.(r, c, FACE)}
            />
            <TileHeatmap
              stats={stats}
              kind="persons"
              onCellClick={(r, c) => onOpenTileCoverage?.(r, c, PERSON)}
              printBreak
            />
          </div>
        </Section>
      ) : null}

      <Section title="Labels">
        <LabelsExplorer stats={stats} onOpenDetail={onOpenDetail} />
      </Section>
    </div>
  );
};

export default memo(DataExplore);
export {
  tipAt, anchorRowTip, AGE_TIP_WIDTH, AgeTip,
};
